import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../config";


const API = `${API_URL}/api/grados`;
const API_NIVELES = `${API_URL}/api/niveles`;

const GestionarGrados = () => {
  const [grados, setGrados] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [nuevoGrado, setNuevoGrado] = useState("");
  const [nuevoNivel, setNuevoNivel] = useState("");

  const [nuevoNivelNombre, setNuevoNivelNombre] = useState("");

  const [gradoEditando, setGradoEditando] = useState(null);
  const [nivelEditando, setNivelEditando] = useState("");

  /* ================= CARGAR ================= */
  const fetchData = async () => {
    try {
      setLoading(true);

      const [gradosRes, nivelesRes] = await Promise.all([
        axios.get(API),
        axios.get(API_NIVELES),
      ]);

      setGrados(gradosRes.data || []);
      setNiveles(nivelesRes.data || []);

      if (!nuevoNivel && nivelesRes.data.length > 0) {
        setNuevoNivel(nivelesRes.data[0].id);
      }

    } catch (err) {
      console.error(err);
      alert("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= CRUD NIVELES ================= */
  const agregarNivel = async () => {
    if (!nuevoNivelNombre.trim()) return;

    await axios.post(API_NIVELES, {
      nombre: nuevoNivelNombre,
    });

    setNuevoNivelNombre("");
    fetchData();
  };

  const editarNivel = async (n) => {
    const nuevo = prompt("Nuevo nombre:", n.nombre);
    if (!nuevo) return;

    await axios.post(`${API_NIVELES}/${n.id}`, {
      nombre: nuevo,
    });

    fetchData();
  };

  const toggleNivel = async (n) => {
    await axios.post(`${API_NIVELES}/${n.id}/activo`, {
      activo: n.activo ? 0 : 1,
    });

    fetchData();
  };

  const eliminarNivel = async (id) => {
    if (!window.confirm("¿Eliminar nivel?")) return;

    try {
      await axios.post(`${API_NIVELES}/${id}/eliminar`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || "Error al eliminar nivel");
    }
  };

  /* ================= CRUD GRADOS ================= */
  const agregarGrado = async () => {
    if (!nuevoGrado.trim()) return;

    await axios.post(API, {
      nombre: nuevoGrado,
      nivel_id: nuevoNivel,
    });

    setNuevoGrado("");
    fetchData();
  };

  const toggleActivo = async (id, activo) => {
    await axios.post(`${API}/${id}/activo`, {
      activo: activo ? 0 : 1,
    });

    setGrados((prev) =>
      prev.map((g) =>
        g.id === id ? { ...g, activo: !activo } : g
      )
    );
  };

  const eliminarGrado = async (id) => {
    if (!window.confirm("¿Eliminar grado?")) return;

    await axios.post(`${API}/${id}/eliminar`);
    setGrados((prev) => prev.filter((g) => g.id !== id));
  };

  const abrirEdicion = (g) => {
    setGradoEditando(g);
    setNivelEditando(g.nivel_id);
  };

  const guardarEdicion = async () => {
    await axios.post(`${API}/${gradoEditando.id}`, {
      nombre: gradoEditando.nombre,
      nivel_id: nivelEditando,
    });

    setGradoEditando(null);
    setNivelEditando("");
    fetchData();
  };

  /* ================= AGRUPAR GRADOS POR NIVEL ================= */
  const gradosPorNivel = [
    ...niveles.map((n) => ({
      nivel: n,
      grados: grados.filter((g) => g.nivel_id === n.id),
    })),
    {
      nivel: null,
      grados: grados.filter((g) => !niveles.some((n) => n.id === g.nivel_id)),
    },
  ].filter((grupo) => grupo.grados.length > 0);

  return (
    <div className="p-3 md:p-6 md:max-w-3xl">
      <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center md:text-left">
        Gestión de Grados y Niveles
      </h3>

      {/* ================= NIVELES ================= */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 md:p-5 mb-6">
        <h4 className="font-bold text-slate-700 mb-3">Niveles</h4>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            className="border border-gray-300 p-2 rounded-lg flex-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Nuevo nivel"
            value={nuevoNivelNombre}
            onChange={(e) => setNuevoNivelNombre(e.target.value)}
          />

          <button
            onClick={agregarNivel}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold transition"
          >
            ➕ Agregar
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {niveles.map((n) => (
            <div
              key={n.id}
              className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 bg-slate-50 rounded-lg p-3"
              style={{ borderLeft: `4px solid ${n.activo ? (n.color || "#2563eb") : "#94a3b8"}` }}
            >
              <span className={`font-semibold ${n.activo ? "text-slate-800" : "text-slate-400"}`}>
                {n.nombre}
              </span>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => editarNivel(n)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                >
                  Editar
                </button>

                <button
                  onClick={() => toggleNivel(n)}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                >
                  {n.activo ? "Desactivar" : "Activar"}
                </button>

                <button
                  onClick={() => eliminarNivel(n.id)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                >
                Eliminar
              </button>
            </div>
          </div>
        ))}
        </div>
      </div>

      {/* ================= CREAR GRADO ================= */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 md:p-5 mb-6">
        <h4 className="font-bold text-slate-700 mb-3">Nuevo grado</h4>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="border border-gray-300 p-2 rounded-lg flex-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Nuevo grado"
            value={nuevoGrado}
            onChange={(e) => setNuevoGrado(e.target.value)}
          />

          <select
            className="border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={nuevoNivel}
            onChange={(e) => setNuevoNivel(e.target.value)}
          >
            {niveles.map((n) => (
              <option key={n.id} value={n.id}>
                {n.nombre}
              </option>
            ))}
          </select>

          <button
            onClick={agregarGrado}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold transition"
          >
            ➕ Agregar
          </button>
        </div>
      </div>

      {/* ================= GRADOS — ESCRITORIO (agrupados por nivel) ================= */}
      <div className="hidden md:block bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {loading ? (
          <p className="text-center p-6 text-gray-500">Cargando...</p>
        ) : gradosPorNivel.length === 0 ? (
          <p className="text-center p-6 text-gray-500">No hay grados</p>
        ) : (
          gradosPorNivel.map(({ nivel, grados: gradosNivel }) => (
            <div key={nivel?.id || "sin-nivel"}>
              <div
                className="px-4 py-2 font-bold text-white text-sm flex items-center gap-2"
                style={{ background: nivel?.color || "#64748b" }}
              >
                {nivel?.nombre || "Sin nivel"}
                <span className="text-xs font-semibold bg-white/25 px-2 py-0.5 rounded-full">
                  {gradosNivel.length}
                </span>
              </div>

              {gradosNivel.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100 hover:bg-blue-50/50 transition"
                >
                  <span className={`font-medium ${g.activo ? "text-gray-800" : "text-gray-400"}`}>
                    {g.nombre}
                  </span>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded-full ${
                        g.activo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}
                    >
                      {g.activo ? "Activo" : "Inactivo"}
                    </span>

                    <button
                      onClick={() => abrirEdicion(g)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                    >
                      Editar
                    </button>

                    <button
                      onClick={() => toggleActivo(g.id, g.activo)}
                      className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                    >
                      {g.activo ? "Desactivar" : "Activar"}
                    </button>

                    <button
                      onClick={() => eliminarGrado(g.id)}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* ================= GRADOS — MÓVIL (tarjetas agrupadas por nivel) ================= */}
      <div className="md:hidden flex flex-col gap-4">
        {loading ? (
          <p className="text-center p-6 text-gray-500">Cargando...</p>
        ) : gradosPorNivel.length === 0 ? (
          <p className="text-center p-6 text-gray-500">No hay grados</p>
        ) : (
          gradosPorNivel.map(({ nivel, grados: gradosNivel }) => (
            <div key={nivel?.id || "sin-nivel"}>
              <div
                className="rounded-lg px-3 py-2 font-bold text-white text-sm mb-2 flex items-center gap-2"
                style={{ background: nivel?.color || "#64748b" }}
              >
                {nivel?.nombre || "Sin nivel"}
                <span className="text-xs font-semibold bg-white/25 px-2 py-0.5 rounded-full">
                  {gradosNivel.length}
                </span>
              </div>

              <div className="flex flex-col gap-2">
                {gradosNivel.map((g) => (
                  <div
                    key={g.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 flex flex-col gap-2"
                    style={{ borderLeft: `4px solid ${g.activo ? "#22c55e" : "#ef4444"}` }}
                  >
                    <div className="flex justify-between items-center">
                      <span className={`font-bold ${g.activo ? "text-gray-800" : "text-gray-400"}`}>
                        {g.nombre}
                      </span>
                      <span
                        className={`text-xs font-bold px-2 py-1 rounded-full ${
                          g.activo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}
                      >
                        {g.activo ? "Activo" : "Inactivo"}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => abrirEdicion(g)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition"
                      >
                        Editar
                      </button>

                      <button
                        onClick={() => toggleActivo(g.id, g.activo)}
                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition"
                      >
                        {g.activo ? "Desactivar" : "Activar"}
                      </button>

                      <button
                        onClick={() => eliminarGrado(g.id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ================= MODAL ================= */}
      {gradoEditando && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50 p-4">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4 text-blue-800">✏️ Editar Grado</h3>

            <input
              className="border border-gray-300 p-2 rounded-lg w-full mb-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={gradoEditando.nombre}
              onChange={(e) =>
                setGradoEditando({
                  ...gradoEditando,
                  nombre: e.target.value,
                })
              }
            />

            <select
              className="border border-gray-300 p-2 rounded-lg w-full mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={nivelEditando}
              onChange={(e) => setNivelEditando(e.target.value)}
            >
              {niveles.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.nombre}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setGradoEditando(null)}
                className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg font-semibold transition"
              >
                Cancelar
              </button>

              <button
                onClick={guardarEdicion}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionarGrados;